// Polyfill to prevent Safari Private Browsing QuotaExceededError and SecurityError when using localStorage/sessionStorage.

const makeSafeStorage = (type: 'localStorage' | 'sessionStorage') => {
  let storage: Storage;
  try {
    storage = window[type];
    if (!storage) {
      throw new Error(`${type} is not available`);
    }
    // Test write
    const testKey = `__storage_test_${type}__`;
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return; // Already safe and working!
  } catch (e) {
    console.warn(`[SafeStorage] window.${type} is blocked or throws errors. Activating in-memory fallback.`, e);
  }

  // Fallback storage implementation
  const fallbackStore = new Map<string, string>();
  const fallbackImplementation: Storage = {
    getItem(key: string): string | null {
      return fallbackStore.has(key) ? fallbackStore.get(key)! : null;
    },
    setItem(key: string, value: string): void {
      fallbackStore.set(key, String(value));
    },
    removeItem(key: string): void {
      fallbackStore.delete(key);
    },
    clear(): void {
      fallbackStore.clear();
    },
    key(index: number): string | null {
      return Array.from(fallbackStore.keys())[index] || null;
    },
    get length(): number {
      return fallbackStore.size;
    }
  };

  try {
    Object.defineProperty(window, type, {
      value: fallbackImplementation,
      configurable: true,
      writable: true
    });
  } catch (err) {
    console.error(`[SafeStorage] Failed to override window.${type} via defineProperty:`, err);
    // Prototype poisoning fallback
    try {
      const proto = type === 'localStorage' ? localStorage : sessionStorage;
      if (proto) {
        const target = Object.getPrototypeOf(proto) || proto;
        target.getItem = fallbackImplementation.getItem;
        target.setItem = fallbackImplementation.setItem;
        target.removeItem = fallbackImplementation.removeItem;
        target.clear = fallbackImplementation.clear;
        target.key = fallbackImplementation.key;
        Object.defineProperty(target, 'length', {
          get: () => fallbackStore.size,
          configurable: true
        });
      }
    } catch (protoErr) {
      console.error(`[SafeStorage] Failed to patch prototype of ${type}:`, protoErr);
    }
  }
};

if (typeof window !== 'undefined') {
  makeSafeStorage('localStorage');
  makeSafeStorage('sessionStorage');
}
export {};
